import { normalizeOrderStatus } from '@emporion/config/statuses'
import type { CustomerOrder, CustomerOrderContact, CustomerOrderCurrency, CustomerOrderInvoice } from './types'

const normalizeOrderContact = (input: unknown): CustomerOrderContact | null => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null
  const raw = input as Record<string, unknown>
  const required = ['firstName', 'lastName', 'addressLine1', 'postalCode', 'city', 'country'] as const
  if (required.some((field) => typeof raw[field] !== 'string' || !raw[field].trim())) return null
  const optional = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : null)
  return {
    firstName: String(raw.firstName).trim(),
    lastName: String(raw.lastName).trim(),
    company: optional(raw.company),
    email: optional(raw.email),
    phone: optional(raw.phone),
    addressLine1: String(raw.addressLine1).trim(),
    addressLine2: optional(raw.addressLine2),
    postalCode: String(raw.postalCode).trim(),
    city: String(raw.city).trim(),
    province: optional(raw.province),
    country: String(raw.country).trim(),
    taxCode: optional(raw.taxCode),
  }
}

const normalizePaymentState = (
  value: unknown,
  status: CustomerOrder['status'],
): CustomerOrder['paymentState'] => {
  if (
    value === 'unpaid' ||
    value === 'pending' ||
    value === 'failed' ||
    value === 'paid' ||
    value === 'cancelled'
  ) {
    return value
  }
  if (status === 'paid' || status === 'shipped') return 'paid'
  if (status === 'cancelled') return 'cancelled'
  return 'unpaid'
}

const normalizePaymentProvider = (value: unknown): CustomerOrder['paymentProvider'] =>
  value === 'stripe' || value === 'bank_transfer' ? value : null

const normalizePaymentStatus = (
  value: unknown,
  status: CustomerOrder['status'],
): CustomerOrder['paymentStatus'] => {
  if (
    value === 'unpaid'
    || value === 'awaiting_bank_transfer'
    || value === 'pending'
    || value === 'paid'
    || value === 'failed'
    || value === 'refunded'
    || value === 'expired'
    || value === 'cancelled'
  ) {
    return value
  }
  if (status === 'paid' || status === 'shipped') return 'paid'
  if (status === 'cancelled') return 'cancelled'
  return 'unpaid'
}

export const normalizeCustomerOrder = (input: Record<string, unknown>): CustomerOrder => ({
  id: String(input.id ?? ''),
  orderNumber: String(input.orderNumber ?? ''),
  userId: String(input.userId ?? ''),
  sourceCartId: String(input.sourceCartId ?? ''),
  status: normalizeOrderStatus(input.status) ?? 'created',
  paymentProvider: normalizePaymentProvider(input.paymentProvider),
  paymentStatus: normalizePaymentStatus(input.paymentStatus, normalizeOrderStatus(input.status) ?? 'created'),
  paymentState: normalizePaymentState(input.paymentState, normalizeOrderStatus(input.status) ?? 'created'),
  currency: (String(input.currency ?? 'EUR') as CustomerOrderCurrency) || 'EUR',
  items: Array.isArray(input.items)
    ? input.items
        .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
        .map((item) => ({
          itemId: String(item.itemId ?? ''),
          productId: String(item.productId ?? ''),
          productSlug: String(item.productSlug ?? ''),
          productName: String(item.productName ?? ''),
          unitPriceSnapshot:
            typeof item.unitPriceSnapshot === 'number' && Number.isFinite(item.unitPriceSnapshot)
              ? item.unitPriceSnapshot
              : 0,
          quantity: typeof item.quantity === 'number' && Number.isFinite(item.quantity) ? item.quantity : 0,
          lineTotal: typeof item.lineTotal === 'number' && Number.isFinite(item.lineTotal) ? item.lineTotal : 0,
          addedAt: String(item.addedAt ?? ''),
          updatedAt: String(item.updatedAt ?? ''),
        }))
    : [],
  itemCount: typeof input.itemCount === 'number' && Number.isFinite(input.itemCount) ? input.itemCount : 0,
  subtotal: typeof input.subtotal === 'number' && Number.isFinite(input.subtotal) ? input.subtotal : 0,
  total: typeof input.total === 'number' && Number.isFinite(input.total) ? input.total : 0,
  createdAt: String(input.createdAt ?? ''),
  updatedAt: String(input.updatedAt ?? ''),
  paidAt: typeof input.paidAt === 'string' ? input.paidAt : null,
  paymentReference: typeof input.paymentReference === 'string' ? input.paymentReference : null,
  paymentMethodLabel: typeof input.paymentMethodLabel === 'string' ? input.paymentMethodLabel : null,
  paymentMethodType: typeof input.paymentMethodType === 'string' ? input.paymentMethodType : null,
  paymentMethodBrand: typeof input.paymentMethodBrand === 'string' ? input.paymentMethodBrand : null,
  paymentMethodLast4: typeof input.paymentMethodLast4 === 'string' ? input.paymentMethodLast4 : null,
  paymentMethodWallet: typeof input.paymentMethodWallet === 'string' ? input.paymentMethodWallet : null,
  shippingContact: normalizeOrderContact(input.shippingContact),
  billingContact: normalizeOrderContact(input.billingContact),
  shippedAt: typeof input.shippedAt === 'string' ? input.shippedAt : null,
  trackingCode: typeof input.trackingCode === 'string' ? input.trackingCode : null,
  trackingUrl: typeof input.trackingUrl === 'string' ? input.trackingUrl : null,
  cancelledAt: typeof input.cancelledAt === 'string' ? input.cancelledAt : null,
  fulfillmentState:
    input.fulfillmentState === 'partially_cancelled' || input.fulfillmentState === 'fully_cancelled'
      ? input.fulfillmentState
      : 'normal',
})

export const normalizeCustomerOrderInvoice = (input: Record<string, unknown>): CustomerOrderInvoice => ({
  id: String(input.id ?? ''),
  orderId: String(input.orderId ?? ''),
  orderNumber: String(input.orderNumber ?? ''),
  invoiceNumber: String(input.invoiceNumber ?? ''),
  invoiceDate: String(input.invoiceDate ?? ''),
  version: typeof input.version === 'number' && Number.isFinite(input.version) ? input.version : 1,
  downloadUrl: String(input.downloadUrl ?? ''),
  fileName: String(input.fileName ?? ''),
  mimeType: String(input.mimeType ?? 'application/pdf'),
  uploadedAt: String(input.uploadedAt ?? ''),
})
